import { Filter } from '../core/models/filter.model';
import { OperationEnum } from '../shared/enums/operation-type.enum';

export type PrimeFilterLogicalOperator = 'and' | 'or';

export interface PrimeFilterConstraint {
  value: string | Date | number | boolean | undefined;
  matchMode?: string | number;
  operator?: PrimeFilterLogicalOperator;
}

export type PrimeFilterMeta =
  | PrimeFilterConstraint
  | PrimeFilterConstraint[]
  | {
      constraints?: PrimeFilterConstraint[];
      operator?: PrimeFilterLogicalOperator;
      matchMode?: string | number;
      value?: string | Date | number | boolean | undefined;
    };

export function buildColumnFiltersFromPrimeNg(
  filters: Record<string, PrimeFilterMeta>,
  fieldMapping: Record<string, string>,
  dateFields: string[],
): { columnFilters: Filter[]; fieldsToRemove: Set<string> } {
  const columnFilters: Filter[] = [];
  const fieldsToRemove = new Set<string>();

  for (const [field, meta] of Object.entries(filters)) {
    const result = buildColumnFilterForField(field, meta, fieldMapping, dateFields);

    if (result.backendField && result.shouldRemove) {
      fieldsToRemove.add(result.backendField);
    }

    if (result.filter) {
      columnFilters.push(result.filter);
    }
  }

  return { columnFilters, fieldsToRemove };
}

export function buildColumnFilterForField(
  field: string,
  meta: PrimeFilterMeta | undefined,
  fieldMapping: Record<string, string>,
  dateFields: string[],
): { filter?: Filter; shouldRemove: boolean; backendField?: string } {
  const backendField = fieldMapping[field] || field;
  const isDateField = dateFields.includes(field);
  const metaObj = meta;
  const metaWithMatchMode = metaObj as { matchMode?: string | number; value?: unknown } | undefined;
  // Check for "in" matchMode with array value (multi-select filters) before checking constraints
  const isInMatchModeWithArray =
    (metaWithMatchMode?.matchMode === 'in' || metaWithMatchMode?.matchMode === 'In') &&
    Array.isArray(metaWithMatchMode?.value) &&
    (metaWithMatchMode.value as unknown[]).length > 0;

  const constraints = getPrimeNgConstraints(metaObj);
  if (!constraints.length && !isInMatchModeWithArray) {
    return { shouldRemove: true, backendField };
  }

  const groupCondition = getGroupConditionFromMeta(metaObj);

  const subFilters = buildSubFiltersForField(constraints, backendField, groupCondition, isDateField, metaObj);

  if (!subFilters.length) {
    return { shouldRemove: true, backendField };
  }

  const filter: Filter = {
    field: backendField,
    condition: groupCondition as 'AND' | 'OR',
    subFilters,
  };

  return { filter, shouldRemove: false, backendField };
}

export function getPrimeNgConstraints(metaObj: PrimeFilterMeta | undefined): PrimeFilterConstraint[] {
  if (!metaObj) {
    return [];
  }

  if (Array.isArray(metaObj)) {
    return metaObj;
  }

  const withConstraints = metaObj as { constraints?: PrimeFilterConstraint[]; value?: unknown; matchMode?: string | number };

  if (Array.isArray(withConstraints.constraints)) {
    return withConstraints.constraints;
  }

  // For "in" matchMode with array values, return empty array - will be handled in buildSubFiltersForField
  if ((withConstraints.matchMode === 'in' || withConstraints.matchMode === 'In') && Array.isArray(withConstraints.value)) {
    return [];
  }

  if (withConstraints.value !== undefined && withConstraints.value !== null) {
    return [withConstraints as PrimeFilterConstraint];
  }

  return [];
}

export function getGroupConditionFromMeta(metaObj: PrimeFilterMeta | undefined): 'AND' | 'OR' {
  if (!metaObj) {
    return 'AND';
  }

  // For "in" matchMode (multi-select), use OR condition
  const metaWithMatchMode = metaObj as { matchMode?: string | number } | undefined;
  if (metaWithMatchMode?.matchMode === 'in' || metaWithMatchMode?.matchMode === 'In') {
    return 'OR';
  }

  const constraints = getPrimeNgConstraints(metaObj);
  if (constraints.some((constraint) => constraint.matchMode === 'in' || constraint.matchMode === 'In')) {
    return 'OR';
  }

  let op: PrimeFilterLogicalOperator | undefined;

  if (Array.isArray(metaObj) && metaObj.length > 0) {
    op = metaObj[0]?.operator;
  } else {
    const obj = metaObj as { operator?: PrimeFilterLogicalOperator };
    op = obj.operator;
  }

  return op === 'or' ? 'OR' : 'AND';
}

/** Calendar date for range filters: local Y/M/D for Date values; Y-M-D strings use those components as UTC date (same as normalizeFilterValue / convertToUTC('YYYY-MM-DD')). */
function getCalendarDatePartsForRangeFilter(v: unknown): { y: number; m: number; d: number } | null {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return { y: v.getFullYear(), m: v.getMonth(), d: v.getDate() };
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const [year, month, day] = v.split('-').map(Number);
      return { y: year, m: month - 1, d: day };
    }
    const parsed = new Date(v);
    if (!Number.isNaN(parsed.getTime())) {
      return { y: parsed.getFullYear(), m: parsed.getMonth(), d: parsed.getDate() };
    }
  }
  if (typeof v === 'number' && !Number.isNaN(v)) {
    const parsed = new Date(v);
    if (!Number.isNaN(parsed.getTime())) {
      return { y: parsed.getFullYear(), m: parsed.getMonth(), d: parsed.getDate() };
    }
  }
  return null;
}

function utcInclusiveRangeStartIso(parts: { y: number; m: number; d: number }): string {
  return new Date(Date.UTC(parts.y, parts.m, parts.d)).toISOString();
}

function utcInclusiveRangeEndIso(parts: { y: number; m: number; d: number }): string {
  return new Date(Date.UTC(parts.y, parts.m, parts.d, 23, 59, 59, 999)).toISOString();
}

export function buildSubFiltersForField(
  constraints: PrimeFilterConstraint[],
  backendField: string,
  groupCondition: 'AND' | 'OR',
  isDateField: boolean,
  metaObj: PrimeFilterMeta | undefined,
): Filter[] {
  const subFilters: Filter[] = [];

  const metaWithMatchMode = metaObj as { matchMode?: string | number; value?: unknown } | undefined;
  const isPhoneField = backendField.toLowerCase().includes('phone');
  const matchMode = metaWithMatchMode?.matchMode ?? (isPhoneField ? 'contains' : 'equals');
  const isInMatchMode = matchMode === 'in' || matchMode === 'In';
  const isBetweenMatchMode = matchMode === 'between' || matchMode === 'Between';

  if (isBetweenMatchMode && Array.isArray(metaWithMatchMode?.value)) {
    const [rawMin, rawMax] = metaWithMatchMode.value as Array<string | number | Date | null | undefined>;

    if (isDateField) {
      const startParts = getCalendarDatePartsForRangeFilter(rawMin);
      const endParts = getCalendarDatePartsForRangeFilter(rawMax);
      if (startParts) {
        const utcStart = utcInclusiveRangeStartIso(startParts);
        if (utcStart) {
          subFilters.push({
            field: backendField,
            operation: OperationEnum.GreaterOrEquals,
            value: utcStart,
            condition: 'AND',
          });
        }
      }
      if (endParts) {
        const utcEnd = utcInclusiveRangeEndIso(endParts);
        if (utcEnd) {
          subFilters.push({
            field: backendField,
            operation: OperationEnum.LessOrEquals,
            value: utcEnd,
            condition: 'AND',
          });
        }
      }
    } else {
      const min = rawMin === null || rawMin === undefined || rawMin === '' ? null : Number(rawMin);
      const max = rawMax === null || rawMax === undefined || rawMax === '' ? null : Number(rawMax);
      if (min !== null && !Number.isNaN(min)) {
        subFilters.push({
          field: backendField,
          operation: OperationEnum.GreaterOrEquals,
          value: min,
          condition: 'AND',
        });
      }
      if (max !== null && !Number.isNaN(max)) {
        subFilters.push({
          field: backendField,
          operation: OperationEnum.LessOrEquals,
          value: max,
          condition: 'AND',
        });
      }
    }
    return subFilters;
  }

  // Handle "in" matchMode with array values (for multi-select filters)
  if (isInMatchMode && metaWithMatchMode?.value && Array.isArray(metaWithMatchMode.value)) {
    const arrayValue = metaWithMatchMode.value as unknown[];
    for (const item of arrayValue) {
      const normalizedValue = normalizeFilterValue(item as string | Date | number | boolean | undefined, isDateField);
      if (hasValidFilterValue(normalizedValue)) {
        subFilters.push({
          field: backendField,
          operation: OperationEnum.Equals,
          value: normalizedValue,
          condition: 'OR', // Use OR for multi-select "in" filters
        });
      }
    }
    return subFilters;
  }

  // Handle regular constraints
  for (const constraint of constraints) {
    let value = constraint.value;
    const constraintMatchMode = constraint.matchMode ?? matchMode;
    const constraintIsBetween = constraintMatchMode === 'between' || constraintMatchMode === 'Between';

    if (constraintIsBetween && Array.isArray(value)) {
      const [rawMin, rawMax] = value as Array<string | number | Date | null | undefined>;

      if (isDateField) {
        const startParts = getCalendarDatePartsForRangeFilter(rawMin);
        const endParts = getCalendarDatePartsForRangeFilter(rawMax);
        if (startParts) {
          const utcStart = utcInclusiveRangeStartIso(startParts);
          if (utcStart) {
            subFilters.push({
              field: backendField,
              operation: OperationEnum.GreaterOrEquals,
              value: utcStart,
              condition: 'AND',
            });
          }
        }
        if (endParts) {
          const utcEnd = utcInclusiveRangeEndIso(endParts);
          if (utcEnd) {
            subFilters.push({
              field: backendField,
              operation: OperationEnum.LessOrEquals,
              value: utcEnd,
              condition: 'AND',
            });
          }
        }
      } else {
        const min = rawMin === null || rawMin === undefined || rawMin === '' ? null : Number(rawMin);
        const max = rawMax === null || rawMax === undefined || rawMax === '' ? null : Number(rawMax);
        if (min !== null && !Number.isNaN(min)) {
          subFilters.push({
            field: backendField,
            operation: OperationEnum.GreaterOrEquals,
            value: min,
            condition: 'AND',
          });
        }
        if (max !== null && !Number.isNaN(max)) {
          subFilters.push({
            field: backendField,
            operation: OperationEnum.LessOrEquals,
            value: max,
            condition: 'AND',
          });
        }
      }
      continue;
    }

    // Handle array values in constraints (for "in" matchMode or when value is an array)
    if ((constraintMatchMode === 'in' || constraintMatchMode === 'In' || Array.isArray(value)) && Array.isArray(value)) {
      for (const item of value as unknown[]) {
        const normalizedValue = normalizeFilterValue(item as string | Date | number | boolean | undefined, isDateField);
        if (hasValidFilterValue(normalizedValue)) {
          subFilters.push({
            field: backendField,
            operation: OperationEnum.Equals,
            value: normalizedValue,
            condition: 'OR', // Use OR for multi-select "in" filters
          });
        }
      }
      continue;
    }

    value = normalizeFilterValue(value, isDateField);

    if (!hasValidFilterValue(value)) {
      continue;
    }

    subFilters.push({
      field: backendField,
      operation: mapMatchModeToOperation(constraintMatchMode),
      value,
      condition: groupCondition,
    });
  }

  return subFilters;
}

export function normalizeFilterValue(value: string | Date | number | boolean | undefined, isDateField: boolean): string | Date | number | boolean | undefined {
  if (!isDateField) {
    return value;
  }

  if (value instanceof Date) {
    return toUtcDateOnlyIso(value);
  }

  if (typeof value === 'string' && value) {
    if (isDateOnlyString(value)) {
      return toUtcDateOnlyIsoString(value);
    }

    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }

    return value;
  }

  return value;
}

function isDateOnlyString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function toUtcDateOnlyIso(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
}

function toUtcDateOnlyIsoString(value: string): string {
  const [year, month, day] = value.split('-').map((part) => Number(part));
  return new Date(Date.UTC(year, month - 1, day)).toISOString();
}

export function hasValidFilterValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (value instanceof Date && isNaN(value.getTime())) {
    return false;
  }

  return true;
}

export function mapMatchModeToOperation(matchMode: string | number): number {
  if (typeof matchMode === 'number') {
    return matchMode;
  }

  const mode = matchMode.toString().toLowerCase();

  const map: Record<string, number> = {
    startswith: OperationEnum.StartWith,
    contains: OperationEnum.Contains,
    endswith: OperationEnum.EndWith,
    equals: OperationEnum.Equals,
    dateis: OperationEnum.Equals,
    notequals: OperationEnum.Different,
    dateisnot: OperationEnum.Different,
    lt: OperationEnum.Less,
    datebefore: OperationEnum.Less,
    lte: OperationEnum.LessOrEquals,
    gt: OperationEnum.Greater,
    dateafter: OperationEnum.Greater,
    gte: OperationEnum.GreaterOrEquals,
  };

  return map[mode] ?? OperationEnum.Contains;
}
